"use client";

import React, { createContext, useContext, useState, ReactNode, useCallback, useMemo } from 'react';

export interface BreadcrumbItem {
  id: string;
  title: string;
  href?: string;
  icon?: React.ComponentType<{ className?: string }>;
}

export interface HeaderAction {
  id: string;
  label: string;
  icon?: ReactNode;
  onClick: () => void;
  variant?: "default" | "outline" | "secondary" | "ghost" | "destructive";
  disabled?: boolean;
}

interface HeaderPortalContextType {
  // Content state
  title: string | null;
  subtitle: string | null;
  breadcrumbs: BreadcrumbItem[];
  toolbarContent: ReactNode | null;
  rightContent: ReactNode | null;
  actions: HeaderAction[];
  
  // Setters
  setTitle: (title: string | null) => void;
  setSubtitle: (subtitle: string | null) => void;
  setBreadcrumbs: (breadcrumbs: BreadcrumbItem[]) => void;
  setToolbarContent: (content: ReactNode | null) => void;
  setRightContent: (content: ReactNode | null) => void;
  setActions: (actions: HeaderAction[]) => void;
  
  // Convenience methods
  clearAll: () => void;
}

const HeaderPortalContext = createContext<HeaderPortalContextType | undefined>(undefined);

export function HeaderPortalProvider({ children }: { children: ReactNode }) {
  const [title, setTitle] = useState<string | null>(null);
  const [subtitle, setSubtitle] = useState<string | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);
  const [toolbarContent, setToolbarContent] = useState<ReactNode | null>(null);
  const [rightContent, setRightContent] = useState<ReactNode | null>(null);
  const [actions, setActions] = useState<HeaderAction[]>([]);

  const clearAll = useCallback(() => {
    setTitle(null);
    setSubtitle(null);
    setBreadcrumbs([]);
    setToolbarContent(null);
    setRightContent(null);
    setActions([]);
  }, []);

  // Memoize the context value to prevent recreation on every render
  const value: HeaderPortalContextType = useMemo(() => ({
    title,
    subtitle,
    breadcrumbs,
    toolbarContent,
    rightContent,
    actions,
    setTitle,
    setSubtitle,
    setBreadcrumbs,
    setToolbarContent,
    setRightContent,
    setActions,
    clearAll,
  }), [title, subtitle, breadcrumbs, toolbarContent, rightContent, actions, clearAll]);

  return (
    <HeaderPortalContext.Provider value={value}>
      {children}
    </HeaderPortalContext.Provider>
  );
}

export function useHeaderPortal(): HeaderPortalContextType {
  const context = useContext(HeaderPortalContext);
  if (context === undefined) {
    throw new Error('useHeaderPortal must be used within a HeaderPortalProvider');
  }
  return context;
}